import { useEffect, useRef, useState } from 'react';
import type { Lang } from './lang';
import { LANG_CONFIG } from './lang';

const ChevronDown = ({ open }: { open: boolean }) => (
  <svg
    width="10" height="10" viewBox="0 0 24 24"
    fill="none" stroke="currentColor" strokeWidth="2.5"
    strokeLinecap="round" strokeLinejoin="round"
    style={{ transition: 'transform 0.18s', transform: open ? 'rotate(180deg)' : 'none', flexShrink: 0 }}
  >
    <path d="m6 9 6 6 6-6" />
  </svg>
);

const Tick = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#6366F1" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
    <path d="m4 12 5 5L20 6" />
  </svg>
);

interface Props {
  lang: Lang;
  setLang: (l: Lang) => void;
  langs: Lang[];
  /** Extra CSS class applied to the root wrapper — use for positioning */
  className?: string;
}

/**
 * Floating language picker.
 * Shows a pill button with the current flag + code.
 * Clicking opens a dropdown with all supported languages.
 * Renders nothing when only one language is supported.
 */
export const LangDropdown = ({ lang, setLang, langs, className = '' }: Props) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  // Close when clicking outside
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // Don't render if only one language is available
  if (langs.length <= 1) return null;

  const current = LANG_CONFIG[lang];

  return (
    <div className={`ld-root${className ? ` ${className}` : ''}`} ref={rootRef}>
      {/* Trigger button */}
      <button
        className="ld-trigger"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={`Language: ${current.label}`}
      >
        <span className="ld-flag" role="img" aria-hidden="true">{current.flag}</span>
        <span className="ld-code">{current.code}</span>
        <ChevronDown open={open} />
      </button>

      {/* Dropdown panel */}
      {open && (
        <div className="ld-dropdown" role="listbox" aria-label="Select language">
          {langs.map((l) => {
            const cfg = LANG_CONFIG[l];
            const active = l === lang;
            return (
              <button
                key={l}
                role="option"
                aria-selected={active}
                className={`ld-option${active ? ' active' : ''}`}
                onClick={() => { setLang(l); setOpen(false); }}
              >
                <span className="ld-flag" role="img" aria-hidden="true">{cfg.flag}</span>
                <span className="ld-option-label">{cfg.label}</span>
                {active && <Tick />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
